'use client';

import { useEffect, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import { AlertTriangle } from 'lucide-react';

interface FeatureFlags {
  integrationsDisabled: boolean;
  metaWhatsAppReviewed: boolean;
  metaMessengerReviewed: boolean;
  metaInstagramReviewed: boolean;
}

/**
 * Renders a non-dismissible banner when MESSAGING_INTEGRATIONS_DISABLED=true.
 * Mounted at the top of the Connected Accounts tab (and is safe to mount on
 * other surfaces too — the API call is cached at the route layer).
 */
export function MessagingKillSwitchBanner() {
  const t = useT();
  const [flags, setFlags] = useState<FeatureFlags | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/messaging/feature-flags', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled && d) setFlags(d as FeatureFlags);
      })
      .catch(() => { /* silent */ });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!flags?.integrationsDisabled) return null;

  return (
    <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 mb-4 flex items-start gap-3">
      <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" aria-hidden="true" />
      <div className="text-sm text-amber-700 dark:text-amber-200">
        <p className="font-medium">{t('messagingBanner.paused')}</p>
        <p className="text-xs opacity-80">
          Inbound webhooks are accepted but not processed, and outbound messages
          will fail. We&rsquo;ll re-enable automatically once the underlying issue is
          resolved &mdash; no action required from you.
        </p>
      </div>
    </div>
  );
}
